import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';

type Status = 'loading' | 'success' | 'error' | 'already-verified';

export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await api.get(`/auth/verify-email/${token}`);
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
        setTimeout(() => navigate('/login?verified=true'), 3000);
      } catch (error: any) {
        const code = error?.response?.data?.error?.code;
        if (code === 'ALREADY_VERIFIED') {
          setStatus('already-verified');
        } else {
          setStatus('error');
          setMessage(error?.response?.data?.error?.message || 'Verification failed. Link may have expired.');
        }
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-xl p-12 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-primary-600 mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email...</h1>
            <p className="text-gray-500">Please wait a moment</p>
          </>
        )}
        {status === 'success' && (
          <>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Email Verified! 🎉</h1>
            <p className="text-gray-600 mb-8">Your email has been verified successfully. Redirecting you to login...</p>
            <Link to="/login" className="block w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-semibold hover:shadow-lg transition">Go to Login</Link>
          </>
        )}
        {status === 'already-verified' && (
          <>
            <CheckCircle className="w-20 h-20 text-blue-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Already Verified</h1>
            <p className="text-gray-600 mb-8">Your email is already verified. You can log in to your account.</p>
            <Link to="/login" className="block w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-semibold hover:shadow-lg transition">Go to Login</Link>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Verification Failed</h1>
            <p className="text-gray-600 mb-8">{message}</p>
            <div className="space-y-3">
              <Link to="/register" className="block w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-semibold hover:shadow-lg transition">Register Again</Link>
              <Link to="/login" className="block w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition">Back to Login</Link>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
